/**
 * ATL-1445 stage 3, P6 — the local view's published state: one Jotai atom holding an immutable
 * snapshot of what this device's unsent (and acknowledged-but-not-yet-confirmed) master writes add
 * to each surface. `AtomsProvider` derives the read-only `productsDictAtom` and `dailyStatsAtom` from
 * it; `OperationsList` and the client search read its rows.
 *
 * Kept apart from `localView.ts` (the engine) so `AtomsProvider` imports only this atom and the pure
 * fold functions — not the engine, the outbox store or BroadcastChannel.
 *
 * EMPTY IS `null`, per surface, and the empty snapshot is ONE frozen constant. That is the whole of
 * how today's sessions stay byte-identical: every derivation starts with `if (overlay === null)
 * return server`, so with no overlay the derived atom returns the server atom's own object — the same
 * reference, never a copy — and nothing that memoises on identity re-renders. A surface's overlay
 * object is replaced only when THAT surface's contribution changes, so a new sale row does not
 * recompute the products dict.
 */
import { atom } from 'jotai'

import type { JsonObject, JsonPrimitive } from './outboxEntry'
import type { RecordOp } from './localEffects'

/** One product's local operations, in FIFO order (records and stock deltas interleaved). */
export type ProductOverlayOp =
  | { readonly kind: 'stock'; readonly storeId: string; readonly delta: number }
  | { readonly kind: 'record'; readonly op: RecordOp; readonly fields: Readonly<JsonObject> }

/** Keyed by the products dict key. `null` when nothing is pending. */
export type ProductsOverlay = ReadonlyMap<string, readonly ProductOverlayOp[]>

export interface DailyStatOverlayOp {
  readonly dailyStatId: string
  readonly businessDay: string
  readonly increments: Readonly<Record<string, number>>
  readonly assign: Readonly<Record<string, JsonPrimitive>>
  /** Fields deleted from the day's entry, after `assign` (P9b). */
  readonly remove: readonly string[]
}

export interface DailyStatsOverlay {
  /** Per store, in FIFO order. */
  readonly byStore: ReadonlyMap<string, readonly DailyStatOverlayOp[]>
  /**
   * The store's current business day (`YYYY-MM-DD`) when the snapshot was built, or `null` when it
   * could not be computed. Used ONLY for a store the server has no entry for: an op is shown there
   * when it books to today (a shift opened offline before the day's first server row, invariant 11).
   */
  readonly todayByStore: ReadonlyMap<string, string | null>
}

/** A row a list surface shows before the server returns it. */
export interface PendingRow {
  readonly id: string
  readonly store: string | null
  /** ISO instant, or `null`. */
  readonly date: string | null
  readonly fields: Readonly<JsonObject>
}

export type RowCollection = 'sales' | 'clients'
export type RowsOverlay = Readonly<Record<RowCollection, readonly PendingRow[]>>

export interface LocalViewSnapshot {
  readonly products: ProductsOverlay | null
  readonly dailyStats: DailyStatsOverlay | null
  readonly rows: RowsOverlay | null
}

export const EMPTY_LOCAL_VIEW: LocalViewSnapshot = Object.freeze({ products: null, dailyStats: null, rows: null })

/**
 * The published snapshot. WRITTEN ONLY BY `localView.ts` (the engine's `publish`); `localViewWriters.test.js`
 * fails on any other writer.
 */
export const localViewAtom = atom<LocalViewSnapshot>(EMPTY_LOCAL_VIEW)

/** Per-surface slices: a derived atom re-evaluates only when ITS slice changes identity. */
export const localProductsOverlayAtom = atom((get) => get(localViewAtom).products)
export const localDailyStatsOverlayAtom = atom((get) => get(localViewAtom).dailyStats)
export const localRowsOverlayAtom = atom((get) => get(localViewAtom).rows)
